import {
  Box,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Heading,
  HStack,
  SimpleGrid,
} from "@chakra-ui/react";
import { HiOutlinePlus, HiOutlineTrash } from "react-icons/hi";
import CodesetSelect from "../../CodesetSelect";
import UuidPreview from "../../UuidPreview";
import LocationsEditor from "../../LocationsEditor";
import ClassificationsEditor from "../../ClassificationsEditor";
import AmountsEditor from "../../AmountsEditor";
import DateTimesEditor from "../../DateTimesEditor";
import { createEmptyRegulatoryProvision } from "../../utils/placementDefaults";

const RegulatoryProvisionEditor = ({
  provision,
  provisionIndex,
  onUpdate,
  onRemove,
}) => {
  const updateField = (field, value) => {
    onUpdate(provisionIndex, field, value);
  };

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} mb={6} borderColor="gray.500">
      <UuidPreview uuid={provision.id} />

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
        <CodesetSelect
          label="Type"
          codesetCode="7511"
          value={provision.type ? { code: provision.type, description: "" } : null}
          onChange={(value) => updateField("type", value.code)}
        />

        <FormControl>
          <FormLabel>Description</FormLabel>
          <Input
            value={provision.description || ""}
            onChange={(e) => updateField("description", e.target.value)}
          />
        </FormControl>
      </SimpleGrid>

      <FormControl mb={4}>
        <FormLabel>Regulatory Authority</FormLabel>
        <Input
          value={provision.regulatoryAuthority || ""}
          onChange={(e) => updateField("regulatoryAuthority", e.target.value)}
        />
      </FormControl>

      <Box mb={4}>
        <Heading size="sm" mb={2}>
          Regulatory Risk Locations
        </Heading>
        <LocationsEditor
          filter="#RegulatoryProvision"
          locations={provision.regulatoryRiskLocations || []}
          onChange={(locations) =>
            updateField("regulatoryRiskLocations", locations)
          }
        />
      </Box>

      <Box mb={4}>
        <Heading size="sm" mb={2}>
          Classifications
        </Heading>
        <ClassificationsEditor
          filter="#RegulatoryProvision"
          classifications={provision.classifications || []}
          onChange={(classifications) =>
            updateField("classifications", classifications)
          }
        />
      </Box>

      <Box mb={4}>
        <Heading size="sm" mb={2}>
          Amounts
        </Heading>
        <AmountsEditor
          filter="#RegulatoryProvision"
          amounts={provision.amounts || []}
          onChange={(amounts) => updateField("amounts", amounts)}
        />
      </Box>

      <Box mb={4}>
        <Heading size="sm" mb={2}>
          DateTimes
        </Heading>
        <DateTimesEditor
          dateTimes={provision.dateTimes || []}
          onChange={(dateTimes) => updateField("dateTimes", dateTimes)}
        />
      </Box>

      <HStack justify="flex-end" mt={4}>
        <IconButton
          size="md"
          icon={<HiOutlineTrash />}
          aria-label="Delete Regulatory Provision"
          onClick={() => onRemove(provisionIndex)}
        />
      </HStack>
    </Box>
  );
};

const RegulatoryProvisionsEditor = ({ regulatoryProvisions = [], onChange }) => {
  const updateProvision = (provisionIndex, field, value) => {
    const updated = [...regulatoryProvisions];
    updated[provisionIndex] = {
      ...updated[provisionIndex],
      [field]: value,
    };
    onChange(updated);
  };

  const addProvision = () => {
    onChange([...regulatoryProvisions, createEmptyRegulatoryProvision()]);
  };

  const removeProvision = (provisionIndex) => {
    const updated = [...regulatoryProvisions];
    updated.splice(provisionIndex, 1);
    onChange(updated);
  };

  return (
    <Box>
      <HStack alignItems="center" mb={4}>
        <Heading size="md">Regulatory Provisions</Heading>
        <IconButton
          icon={<HiOutlinePlus />}
          size="md"
          aria-label="Add Regulatory Provision"
          onClick={addProvision}
        />
      </HStack>

      {regulatoryProvisions.map((provision, provisionIndex) => (
        <RegulatoryProvisionEditor
          key={provision.id}
          provision={provision}
          provisionIndex={provisionIndex}
          onUpdate={updateProvision}
          onRemove={removeProvision}
        />
      ))}
    </Box>
  );
};

export default RegulatoryProvisionsEditor;
